import React, { useImperativeHandle, forwardRef, useCallback } from 'react';
import { MasterFlightRecord, StationSector, makeDefaultStation } from '../../../../../utils/types';
import { StationSectorCard } from './components';
import { useStationValidation } from './hooks';
import { useDayChangeCalculator } from '../../../../../hooks';

interface StationsTabProps {
  draft: MasterFlightRecord;
  setDraft: React.Dispatch<React.SetStateAction<MasterFlightRecord>>;
}

export interface StationsTabRef {
  validateAllStations: () => boolean; // Returns true if there are errors
  resetValidation: () => void;
}

/**
 * StationsTab - Simplified and Modularized
 *
 * Manages station sectors with collapse/expand functionality
 * Each sector contains departure station, arrival station, and aircraft details
 */
export const StationsTab = forwardRef<StationsTabRef, StationsTabProps>(
  ({ draft, setDraft }, ref) => {
  const [openSectors, setOpenSectors] = React.useState<boolean[]>(
    () => draft.stations.map((_, i) => i === 0)
  );
  const sectorRefs = React.useRef<(HTMLDivElement | null)[]>([]);

  const {
    handleBlur,
    getFieldError,
    revalidateIfTouched,
    validateAllStations,
    resetValidation,
  } = useStationValidation();

  // Expose validation methods to parent
  useImperativeHandle(ref, () => ({
    validateAllStations: () => validateAllStations(draft.stations),
    resetValidation,
  }));

  React.useEffect(() => {
    setOpenSectors(prev => {
      if (prev.length === draft.stations.length) {
        return prev;
      }

      return draft.stations.map((_, i) => prev[i] ?? i === draft.stations.length - 1);
    });
  }, [draft.stations.length]);

  const toggleSector = (idx: number) => {
    setOpenSectors(prev => {
      const newOpenSectors = prev.map((open, i) => (i === idx ? !open : open));
      return newOpenSectors;
    });
  };

  const addStation = () => {
    const newIndex = draft.stations.length;
    setOpenSectors(prev => prev.map(() => false).concat(true));
    setDraft(prev => {
      const lastStation = prev.stations[prev.stations.length - 1];
      const newStation = makeDefaultStation();

      // Pre-fill new sector's departure with previous sector's arrival
      if (lastStation) {
        newStation.departureStation = lastStation.arrivalStation || '';
        newStation.passengerTerminalDep = lastStation.passengerTerminalArr || '';
        newStation.utcLocalTimeVariationDep = lastStation.utcLocalTimeVariationArr || '';
        newStation.cd = lastStation.ca ?? 0; // Continue day change
        // Note: std (departure time) is left empty for user to input
      }

      return { ...prev, stations: [...prev.stations, newStation] };
    });

    // Scroll to the new sector header after it's rendered
    setTimeout(() => {
      const newSectorElement = sectorRefs.current[newIndex];
      if (newSectorElement) {
        newSectorElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const removeStation = (i: number) => {
    setOpenSectors(prev => prev.filter((_, idx) => idx !== i));
    setDraft(prev => {
      const next = prev.stations.filter((_, idx) => idx !== i);
      return { ...prev, stations: next.length ? next : [makeDefaultStation()] };
    });
  };

  const updateStationField = (index: number, key: keyof StationSector, value: StationSector[keyof StationSector]) => {
    setDraft(prev => {
      const stations = [...prev.stations];
      stations[index] = { ...stations[index], [key]: value };
      return { ...prev, stations };
    });
  };

  // Auto-calculate day changes when times or UTC variations change
  const handleDayChangeUpdate = useCallback((updatedStations: StationSector[]) => {
    setDraft(prev => ({ ...prev, stations: updatedStations }));
  }, [setDraft]);

  useDayChangeCalculator({
    stations: draft.stations,
    onUpdate: handleDayChangeUpdate,
    enabled: true,
  });

  return (
    <div className="space-y-4">
      {draft.stations.map((station, idx) => (
        <div key={idx} ref={el => { sectorRefs.current[idx] = el }}>
          <StationSectorCard
            station={station}
            sectorIndex={idx}
            flightOwner={draft.flightOwner}
            isOpen={openSectors[idx] ?? false}
            canRemove={draft.stations.length > 1}
            onToggle={() => toggleSector(idx)}
            onRemove={() => removeStation(idx)}
            onUpdate={(key, value) => updateStationField(idx, key, value)}
            getFieldError={getFieldError}
            handleBlur={handleBlur}
            revalidateIfTouched={revalidateIfTouched}
          />
        </div>
      ))}

      <button
        type="button"
        onClick={addStation}
        className="w-full py-3.5 bg-white border-2 border-dashed border-primary-200 rounded-xl text-primary-600 text-base font-semibold cursor-pointer hover:bg-primary-50 hover:border-primary-300 transition-colors"
      >
        + Add Sector {draft.stations.length + 1}
      </button>
    </div>
  );
});

StationsTab.displayName = 'StationsTab';
